import React from 'react';
import {getOneMinute} from 'constants/tests.js';
import {chunkArray} from 'utils/arrayUtils';
import {isTablet} from 'react-device-detect';

import AdminTestPanel from 'components/AdminTestPanel';
import strings from 'strings';
import AdminReadyButton from 'components/AdminReadyButton';
import {TYPES} from '../../constants/index';

const ITEMS_PER_COLUMN = isTablet ? 7 : 10;
const TIMER_DURATION = 60;

class Admin extends AdminTestPanel {
	hardTimeout = null;
	tickInterval = null;
	answerUnlockTimeout = null;
	deadline = null;

	hasFinished = false;
	answerLocked = false;
	waitingAdvanced = false;

	constructor(props) {
		super(props, {
			enableTimers: false,
		});

		this.state = {
			...this.state,
			remainingSeconds: TIMER_DURATION,
			forceWaiting: false,
		};
	}

	componentDidMount() {
		if (super.componentDidMount) {
			super.componentDidMount();
		}

		if (this.state.currentStage === 1) {
			this.resetRunFlags();
			this.startHardTimeout();
		}
	}

	componentDidUpdate(prevProps, prevState) {
		if (
			prevState.currentStage !== this.state.currentStage &&
			this.state.currentStage === 1 &&
			prevState.currentStage === 0
		) {
			this.resetRunFlags();
			this.startHardTimeout();
		}

		if (prevState.currentStep !== this.state.currentStep) {
			this.releaseAnswerLock();
		}

		if (prevState.currentStage === 1 && this.state.currentStage !== 1) {
			this.hasFinished = true;
			this.clearHardTimeout();
			this.releaseAnswerLock();
		}
	}

	componentWillUnmount() {
		this.clearHardTimeout();
		this.clearAnswerUnlockTimeout();
		clearTimeout(this.waitingAdvancedTimeout);

		if (super.componentWillUnmount) {
			super.componentWillUnmount();
		}
	}

	shouldResetOnFreshAttempt = () => true;

	// Retakes restart the one-minute timer and remove waiting state from the previous run.
	getFreshAttemptState = () => ({
		remainingSeconds: TIMER_DURATION,
		forceWaiting: false,
	});

	onFreshAttemptReset = () => {
		this.hasFinished = false;
		this.answerLocked = false;
		this.waitingAdvanced = false;
		this.clearHardTimeout();
		this.clearAnswerUnlockTimeout();
	};

	resetRunFlags = () => {
		this.hasFinished = false;
		this.answerLocked = false;
		this.waitingAdvanced = false;
		this.clearAnswerUnlockTimeout();

		this.setState({
			forceWaiting: false,
			remainingSeconds: TIMER_DURATION,
		});
	};

	clearHardTimeout = () => {
		if (this.hardTimeout) {
			clearTimeout(this.hardTimeout);
			this.hardTimeout = null;
		}

		if (this.tickInterval) {
			clearInterval(this.tickInterval);
			this.tickInterval = null;
		}

		this.deadline = null;
	};

	updateRemainingSeconds = () => {
		if (!this.deadline) return;

		const msLeft = Math.max(0, this.deadline - Date.now());
		const remainingSeconds = Math.ceil(msLeft / 1000);

		if (remainingSeconds !== this.state.remainingSeconds) {
			this.setState({remainingSeconds});
		}

		if (msLeft <= 0) {
			this.finishTestOnce(this.state.results);
		}
	};

	startHardTimeout = () => {
		this.clearHardTimeout();
		this.deadline = Date.now() + TIMER_DURATION * 1000;

		this.updateRemainingSeconds();

		this.tickInterval = setInterval(this.updateRemainingSeconds, 100);

		const msLeft = Math.max(0, this.deadline - Date.now());
		this.hardTimeout = setTimeout(() => {
			this.finishTestOnce(this.state.results);
		}, msLeft);
	};

	clearAnswerUnlockTimeout = () => {
		if (this.answerUnlockTimeout) {
			clearTimeout(this.answerUnlockTimeout);
			this.answerUnlockTimeout = null;
		}
	};

	lockAnswer = () => {
		this.answerLocked = true;
		this.clearAnswerUnlockTimeout();

		this.answerUnlockTimeout = setTimeout(() => {
			if (!this.hasFinished && this.state.currentStage === 1) {
				this.answerLocked = false;
			}
		}, 150);
	};

	releaseAnswerLock = () => {
		this.clearAnswerUnlockTimeout();
		this.answerLocked = false;
	};

	isLastItem = () => {
		const steps = getOneMinute().getSteps();
		return this.state.currentStep >= steps.length - 1;
	};

	prepareFinalAnswer = () => {
		this.hasFinished = true;
		this.answerLocked = true;
		this.clearAnswerUnlockTimeout();
		this.clearHardTimeout();
	};

	finishTestOnce = (results = this.state.results) => {
		console.log('[OM] finishTestOnce start', {
			hasFinished: this.hasFinished,
			currentStage: this.state.currentStage,
			currentStep: this.state.currentStep,
			remainingSeconds: this.state.remainingSeconds,
		});
		if (this.hasFinished) return;

		this.hasFinished = true;
		this.answerLocked = true;
		this.clearAnswerUnlockTimeout();
		this.clearHardTimeout();

		console.log('[OM] calling this.props.nextStage');
		this.setState(
			{
				remainingSeconds: 0,
				forceWaiting: true,
			},
			() => {
				this.props.nextStage(results);
			}
		);

		console.log('[OM] this.props.nextStage returned');
	};

	// Pure check, no side effects: an answer clicked right as the deadline
	// passes must still go through the normal answerCorrect/Wrong/Unanswered
	// flow so it gets merged into results. Triggering finishTestOnce from
	// here (as this used to) would submit results without that last answer
	// and just drop the click on the floor. The 60s hardTimeout/tickInterval
	// already finish the test on their own within ~100ms of the deadline,
	// which is well within this window.
	canAcceptAnswer = () => {
		if (this.hasFinished) return false;
		if (this.state.forceWaiting) return false;
		if (this.answerLocked) return false;
		if (this.state.currentStage !== 1) return false;

		return true;
	};

	answerCorrect() {
		if (!this.canAcceptAnswer()) return;

		if (this.isLastItem()) {
			this.prepareFinalAnswer();
		} else {
			this.lockAnswer();
		}

		const {results, correctAnswers, currentStep} = this.state;
		const newResults = Object.assign({}, results, {
			[currentStep]: {isCorrect: true, isAnswered: true},
		});
		this.setState(
			{
				results: newResults,
				correctAnswers: correctAnswers + 1,
				consecutiveErrors: 0,
			},
			() => {
				if (this.hasFinished) return;
				this.props.nextStep(this.state.results);
			}
		);
	}

	answerWrong() {
		if (!this.canAcceptAnswer()) return;

		if (this.isLastItem()) {
			this.prepareFinalAnswer();
		} else {
			this.lockAnswer();
		}

		const {results, currentStep} = this.state;
		const newResults = Object.assign({}, results, {
			[currentStep]: {isCorrect: false, isAnswered: true},
		});
		this.setState(
			{
				results: newResults,
				wrongAnswers: this.state.wrongAnswers + 1,
				consecutiveErrors: this.state.consecutiveErrors + 1,
			},
			() => {
				if (this.hasFinished) return;
				this.props.nextStep(this.state.results);
			}
		);
	}

	answerUnanswered() {
		if (!this.canAcceptAnswer()) return;

		if (this.isLastItem()) {
			this.prepareFinalAnswer();
		} else {
			this.lockAnswer();
		}

		const {results, currentStep} = this.state;
		const newResults = Object.assign({}, results, {
			[currentStep]: {isCorrect: false, isAnswered: false},
		});
		this.setState(
			{
				results: newResults,
				unanswered: this.state.unanswered + 1,
				consecutiveErrors: this.state.consecutiveErrors + 1,
			},
			() => {
				if (this.hasFinished) return;
				this.props.nextStep(this.state.results);
			}
		);
	}

	handleAnswerCorrect = (e) => {
		e.stopPropagation();
		this.answerCorrect();
	};

	handleAnswerWrong = (e) => {
		e.stopPropagation();
		this.answerWrong();
	};

	handleAnswerUnanswered = (e) => {
		e.stopPropagation();
		this.answerUnanswered();
	};

	handleWaitingNext = () => {
		if (this.waitingAdvanced) return;
		this.waitingAdvanced = true;
		this.props.nextStage();

		// Self-heal instead of a permanent lock: if this click landed while
		// the admin's roomConfig snapshot was momentarily stale (e.g. right
		// as the 60s timer was also finishing the test), nextStage() can
		// silently no-op. Re-enable the button shortly after so a second
		// click can still get through, rather than leaving it dead forever.
		clearTimeout(this.waitingAdvancedTimeout);
		this.waitingAdvancedTimeout = setTimeout(() => {
			this.waitingAdvanced = false;
		}, 1500);
	};

	getTestName = () => {
		return strings.om_testname;
	};

	getClass = (row, index) => {
		const actualIndex = row * ITEMS_PER_COLUMN + index;
		const entry = this.state.results[actualIndex];

		if (!entry) return '';
		if (!entry.isAnswered) return ' unanswered';
		if (entry.isCorrect) return ' correct';

		return ' error';
	};

	cycleAnswerStatus = (row, index) => {
		const actualIndex = row * ITEMS_PER_COLUMN + index;
		const itemsCopy = JSON.parse(JSON.stringify(this.state.results));
		const entry = itemsCopy[actualIndex];

		let currentStatus;
		if (!entry) currentStatus = 'unanswered';
		else if (!entry.isAnswered) currentStatus = 'unanswered';
		else if (entry.isCorrect) currentStatus = 'correct';
		else currentStatus = 'wrong';

		let newState = {};
		if (currentStatus === 'unanswered')
			newState = {isAnswered: true, isCorrect: true};
		else if (currentStatus === 'correct')
			newState = {isAnswered: true, isCorrect: false};
		else if (currentStatus === 'wrong')
			newState = {isAnswered: false, isCorrect: false};

		itemsCopy[actualIndex] = Object.assign(
			{},
			itemsCopy[actualIndex],
			newState
		);

		this.setState({
			results: itemsCopy,
		});
	};

	renderIntro = () => {
		const {ageGroup, testKeyType} = this.props;

		const isNormingTest =
			ageGroup >= 2 ||
			testKeyType === TYPES.ADULT_FOLLOWUP ||
			testKeyType === TYPES.YOUTH_FOLLOWUP;
		return (
			<div>
				<p>{strings.om_admin1}</p>
				<p>{strings.om_admin2}</p>
				<p>{strings.om_admin3}</p>
				<p>{strings.hotkeyCorrect}</p>
				<p>{strings.hotkeyWrong}</p>
				<p>{strings.hotUnanswered2}</p>
				<p>{strings.clickStartWhenReady2}</p>
				<AdminReadyButton
					remoteIsReady={
						isNormingTest ? true : this.props.candidateReady
					}
					onClick={this.props.nextStage}
				/>
			</div>
		);
	};

	renderTest = () => {
		if (this.state.forceWaiting) {
			return this.renderWaiting();
		}
		const {currentStep, remainingSeconds} = this.state;
		const steps = getOneMinute().getSteps();

		const rows = chunkArray(steps, ITEMS_PER_COLUMN);
		const currentRow = Math.floor(currentStep / ITEMS_PER_COLUMN);
		// Admin keeps the previous active-word marker while candidate view stays unhighlighted.
		const currentItem = Math.floor(currentStep % ITEMS_PER_COLUMN);

		const row = rows[currentRow] || null;
		const previousRow = rows[currentRow - 1] || null;
		const nextRow = rows[currentRow + 1] || null;

		return (
			<div className="oneminute-admin">
				<p>{strings.om_admin1}</p>
				<div className="admin-inline">
					<p>
						{strings.formatString(
							strings.taskNumOf,
							currentStep + 1,
							steps.length
						)}{' '}
					</p>
					<p>{`Gjenstående tid: ${remainingSeconds}s`}</p>
				</div>

				<div className="TestWrapper">
					<div className="column previous">
						{previousRow &&
							previousRow.map((item, index) => (
								<p
									key={index}
									className={`item ${this.getClass(
										currentRow - 1,
										index
									)}`}
									onClick={() =>
										this.cycleAnswerStatus(
											currentRow - 1,
											index
										)
									}
								>
									{item}
								</p>
							))}
					</div>

					<div className="column">
						{row &&
							row.map((item, index) => (
								<p
									key={index}
									className={`item${
										currentItem === index ? ' active' : ''
									}${this.getClass(currentRow, index)}`}
									onClick={() =>
										this.cycleAnswerStatus(
											currentRow,
											index
										)
									}
								>
									{item}
								</p>
							))}
					</div>

					<div className="column next">
						{nextRow &&
							nextRow.map((item, index) => (
								<p
									key={index}
									className={`item ${this.getClass(
										currentRow + 1,
										index
									)}`}
									onClick={() =>
										this.cycleAnswerStatus(
											currentRow + 1,
											index
										)
									}
								>
									{item}
								</p>
							))}
					</div>
				</div>

				<button onClick={this.handleAnswerCorrect}>
					{strings.hotkeyButtonCorrect}
				</button>
				<button onClick={this.handleAnswerWrong}>
					{strings.hotkeyButtonWrong}
				</button>
				<button onClick={this.handleAnswerUnanswered}>
					{strings.hotkeyButtonUnanswered}
				</button>
			</div>
		);
	};

	renderWaiting = () => {
		return (
			<div>
				<p>
					{strings.formatString(
						strings.testDoneAndFinished,
						strings.om_testname
					)}
				</p>
				<button onClick={this.handleWaitingNext}>
					{strings.nextTest}
				</button>
			</div>
		);
	};
}

export default Admin;
